import React, { useEffect, useMemo, useState } from "react";
import { shortenRunDisplayId } from "../../../utils/progressDataTransformer";
import {
  REGEN_TARGETS,
  REGEN_DESTINATIONS,
  REGEN_DEFAULT_DESTINATION,
  fetchRunManifest,
} from "./regenerateDeliverables";
import {
  describeRunCollections,
  groupCollectionsByType,
  collectionDirections,
  toggleScopeGroup,
  resolveScopeSelection,
} from "./regenerateDeliverablesPure";

/**
 * "Regenerate deliverables…" — the modal opened from a completed run tile.
 *
 * Checkboxes for the four deliverables (only those the source run's manifest can
 * serve are enabled), a Scope section listing the run's collections grouped by
 * analysis type with the P/N direction visible (all selected by default; All / None,
 * per-type and per-direction quick toggles), the destination (overwrite in place —
 * default — or keep dated copies), a note listing the template ids and the report
 * image that will be used, and the source run id. Submit hands
 * `{ targets, destination, collections }` to the parent — `collections` is the
 * RESOLVED list in manifest order, so the backend never re-derives the scope (see
 * regenerateDeliverables.buildRegeneratePayload).
 */
function RegenerateDeliverablesModal({
  run,
  onClose,
  onSubmit,
  submitting = false,
  error = null,
  loadManifest = fetchRunManifest,
}) {
  const runId = String(run?.run_id || "");
  const [manifest, setManifest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [targets, setTargets] = useState([]);
  const [destination, setDestination] = useState(REGEN_DEFAULT_DESTINATION);
  const [selectedCollections, setSelectedCollections] = useState([]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    setManifest(null);
    loadManifest(runId)
      .then((body) => {
        if (cancelled) return;
        const found = body && body.exists ? body.manifest || {} : null;
        setManifest(found);
        setTargets(found ? [...(found.regenerable_targets || [])] : []);
        // Scope: every collection of the run selected by default.
        setSelectedCollections(found ? describeRunCollections(found).map((d) => d.name) : []);
        setLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        setLoadError(err?.message || "Could not load the run's manifest.");
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [runId, loadManifest]);

  const available = new Set((manifest && manifest.regenerable_targets) || []);
  const toggleTarget = (id) =>
    setTargets((prev) => (prev.includes(id) ? prev.filter((t) => t !== id) : [...prev, id]));

  // ── Scope (which collections) ──
  const collectionDetails = useMemo(() => describeRunCollections(manifest), [manifest]);
  const groups = useMemo(() => groupCollectionsByType(collectionDetails), [collectionDetails]);
  const directions = useMemo(() => collectionDirections(collectionDetails), [collectionDetails]);
  const scope = useMemo(
    () => resolveScopeSelection(collectionDetails, selectedCollections),
    [collectionDetails, selectedCollections],
  );
  const selectedSet = new Set(selectedCollections);
  const toggleCollection = (name) => setSelectedCollections((prev) => toggleScopeGroup(prev, [name]));
  const toggleGroup = (names) => setSelectedCollections((prev) => toggleScopeGroup(prev, names));
  const groupState = (names) => {
    const on = names.filter((n) => selectedSet.has(n)).length;
    return on === 0 ? "off" : on === names.length ? "on" : "partial";
  };
  const allNames = collectionDetails.map((d) => d.name);
  const hasScope = collectionDetails.length > 0;

  const canSubmit =
    !submitting &&
    !loading &&
    Boolean(manifest) &&
    targets.length > 0 &&
    (!hasScope || scope.collections.length > 0);
  const templateIds = (manifest && manifest.analysis_template_ids) || [];
  const reportImage = manifest && manifest.images ? manifest.images.rsa_report : null;

  const shortImage = (ref) => {
    const text = String(ref || "");
    const at = text.indexOf("@sha256:");
    return at >= 0 ? `sha256:${text.slice(at + 8, at + 20)}…` : text;
  };

  return (
    <div
      className="regen-modal-overlay"
      role="dialog"
      aria-modal="true"
      aria-label="Regenerate deliverables"
      onClick={() => !submitting && onClose && onClose()}
    >
      <div className="regen-modal" onClick={(e) => e.stopPropagation()}>
        <div className="regen-modal-header">
          <h3>
            <i className="fa-solid fa-rotate" aria-hidden /> Regenerate deliverables
          </h3>
          <button
            type="button"
            className="regen-modal-close"
            aria-label="Close"
            disabled={submitting}
            onClick={() => onClose && onClose()}
          >
            <i className="fa-solid fa-xmark" aria-hidden />
          </button>
        </div>

        <div className="regen-modal-body">
          <p className="regen-modal-source" title={runId}>
            Source run: <code>{shortenRunDisplayId(runId)}</code>
            {run?.project_id !== undefined && run?.project_id !== null && (
              <span className="regen-modal-project"> · Project {run.project_id}</span>
            )}
          </p>
          <p className="regen-modal-lead">
            Rebuilds the selected deliverables from this run's stored analysis data with
            today's templates and renderers. No analysis is re-run and nothing is billed.
          </p>

          {loading && (
            <p className="regen-modal-status">
              <i className="fa-solid fa-spinner fa-spin" aria-hidden /> Loading the run's manifest…
            </p>
          )}
          {!loading && loadError && <p className="regen-modal-error">{loadError}</p>}
          {!loading && !loadError && !manifest && (
            <p className="regen-modal-error">
              This run has no manifest, so its deliverables cannot be regenerated. Runs finished
              before manifests existed can be backfilled while their workspace still exists.
            </p>
          )}

          <fieldset className="regen-modal-group" disabled={loading || submitting || !manifest}>
            <legend>Deliverables</legend>
            {REGEN_TARGETS.map((t) => {
              const enabled = available.has(t.id);
              return (
                <label
                  key={t.id}
                  className={`regen-modal-option${enabled ? "" : " regen-modal-option-disabled"}`}
                  title={enabled ? t.hint : "Not available for this run"}
                >
                  <input
                    type="checkbox"
                    checked={targets.includes(t.id)}
                    disabled={!enabled}
                    onChange={() => toggleTarget(t.id)}
                  />
                  <span className="regen-modal-option-label">{t.label}</span>
                  <span className="regen-modal-option-hint">{t.hint}</span>
                </label>
              );
            })}
          </fieldset>

          {hasScope && (
            <fieldset
              className="regen-modal-group regen-scope"
              disabled={loading || submitting || !manifest}
              data-testid="regen-scope"
            >
              <legend>Scope</legend>
              <p className="regen-scope-lead">
                Only the selected collections are regenerated; everything else stays as delivered.
              </p>
              <div className="regen-scope-toggles" role="group" aria-label="Scope quick toggles">
                <button
                  type="button"
                  className={`regen-scope-chip regen-scope-chip-${groupState(allNames)}`}
                  onClick={() => toggleGroup(allNames)}
                  title={scope.all ? "Deselect every collection" : "Select every collection"}
                >
                  All
                </button>
                {groups.length > 1 &&
                  groups.map((g) => {
                    const names = g.items.map((d) => d.name);
                    return (
                      <button
                        key={`type-${g.analysisType}`}
                        type="button"
                        className={`regen-scope-chip regen-scope-chip-${groupState(names)}`}
                        onClick={() => toggleGroup(names)}
                        title={`Toggle every ${g.analysisType} collection`}
                        aria-label={`Toggle ${g.analysisType}`}
                      >
                        {g.analysisType}
                      </button>
                    );
                  })}
                {directions.length > 1 &&
                  directions.map((dir) => {
                    const names = collectionDetails.filter((d) => d.direction === dir).map((d) => d.name);
                    return (
                      <button
                        key={`dir-${dir}`}
                        type="button"
                        className={`regen-scope-chip regen-scope-chip-${groupState(names)}`}
                        onClick={() => toggleGroup(names)}
                        title={`Toggle every ${dir === "P" ? "positive" : "negative"} direction collection`}
                        aria-label={`Toggle direction ${dir}`}
                      >
                        Direction {dir}
                      </button>
                    );
                  })}
                <span className="regen-scope-count" aria-live="polite">
                  {scope.collections.length} / {allNames.length} selected
                </span>
              </div>
              <div className="regen-scope-groups">
                {groups.map((g) => (
                  <div key={g.analysisType} className="regen-scope-type">
                    <div className="regen-scope-type-title">{g.analysisType}</div>
                    {g.items.map((d) => (
                      <label key={d.name} className="regen-scope-item" title={d.name}>
                        <input
                          type="checkbox"
                          checked={selectedSet.has(d.name)}
                          onChange={() => toggleCollection(d.name)}
                          aria-label={d.name}
                        />
                        {d.direction ? (
                          <span className={`regen-scope-dir regen-scope-dir-${d.direction}`}>{d.direction}</span>
                        ) : null}
                        <code className="regen-scope-name">{d.name}</code>
                      </label>
                    ))}
                  </div>
                ))}
              </div>
              {scope.collections.length === 0 && (
                <p className="regen-modal-error">Select at least one collection.</p>
              )}
            </fieldset>
          )}

          <fieldset className="regen-modal-group" disabled={loading || submitting || !manifest}>
            <legend>Destination</legend>
            {REGEN_DESTINATIONS.map((d) => (
              <label key={d.id} className="regen-modal-option" title={d.hint}>
                <input
                  type="radio"
                  name="regen-destination"
                  value={d.id}
                  checked={destination === d.id}
                  onChange={() => setDestination(d.id)}
                />
                <span className="regen-modal-option-label">{d.label}</span>
                <span className="regen-modal-option-hint">{d.hint}</span>
              </label>
            ))}
          </fieldset>

          {manifest && (
            <div className="regen-modal-note">
              <div>
                <strong>Templates:</strong>{" "}
                {templateIds.length ? (
                  templateIds.map((id) => (
                    <code key={id} className="regen-modal-code">
                      {id}
                    </code>
                  ))
                ) : (
                  <span>as recorded in the run's envelopes (current Drive templates apply)</span>
                )}
              </div>
              <div>
                <strong>Online report image:</strong>{" "}
                <span>
                  the current <code>rsa_report</code> image renders the report
                  {reportImage ? (
                    <>
                      {" "}
                      (source run used <code className="regen-modal-code">{shortImage(reportImage)}</code>)
                    </>
                  ) : null}
                  .
                </span>
              </div>
            </div>
          )}

          {error && <p className="regen-modal-error">{error}</p>}
        </div>

        <div className="regen-modal-actions">
          <button
            type="button"
            className="regen-modal-btn"
            disabled={submitting}
            onClick={() => onClose && onClose()}
          >
            Cancel
          </button>
          <button
            type="button"
            className="regen-modal-btn regen-modal-btn-primary"
            disabled={!canSubmit}
            onClick={() =>
              onSubmit &&
              onSubmit({
                targets,
                destination,
                // the resolved list in manifest order (every collection when the run
                // has none to choose from: the backend then regenerates the whole run)
                collections: hasScope ? scope.collections : undefined,
              })
            }
          >
            {submitting ? (
              <>
                <i className="fa-solid fa-spinner fa-spin" aria-hidden /> Starting…
              </>
            ) : (
              "Regenerate"
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

export default RegenerateDeliverablesModal;
